<template>
  <div class="grid grid-cols max-w-[1144px] max-w-176 w-full items-start self-stretch">
    <InformationBox
      v-if="!isLoading && currentPolicy?.policyNumber?.length == 0"
      description="Policy not found"
      :type="InformationBoxTypes.ERROR"
    ></InformationBox>
    <FormStepInputs
      v-if="!isLoading && currentPolicy?.policyNumber"
      :step="{
        stepName: name,
        formInputs: content,
      }"
    >
      <template #claimsIncomplete>
        <div>
          <ClaimsPanel
            :fields="claimsIncomplete"
            :title="claimsIncomplete.title"
            :subtitle="claimsIncomplete.subtitle"
            :brief="claimsIncomplete.brief"
            :claimRef="claimsIncomplete.claimRef"
            :claimDetails="claimsIncomplete.claimDetails"
            :expiryDetails="claimsIncomplete.expiryDetails"
            :resumeBtn="claimsIncomplete.resumeBtn"
            @hasIncompleteClaims="(e: boolean) => hasIncompleteClaimsEmit(e)"
          ></ClaimsPanel>
        </div>
      </template>
      <template #manageYourPolicy>
        <div :class="{ 'mt-12': hasIncompleteClaims }">
          <PanelTitle v-if="!isLoading" v-bind="getFormInput(props.content, 'manageYourPolicy')"></PanelTitle>
        </div>
        <div v-if="policyStatusRules" class="racq-max-w-xl5">
          <StatusMessages
            :insurancePolicy="currentPolicy"
            :statusMessages="getFormInputByName(props.content, 'statusMessages')"
            :selfServiceToggles="selfServiceToggles"
            :policyStatusRules="policyStatusRules"
            :renewLink="getFormInput(props.content, 'renewLink')"
          ></StatusMessages>
        </div>
      </template>
      <template #adjustYourCoverLinks>
        <div v-if="filteredAdjustYourCoverLinks?.fields?.length > 0" class="mt-12">
          <LinkPanel
            :fields="filteredAdjustYourCoverLinks"
            :heading="adjustYourCoverLinks.heading"
            :headingDescription="adjustYourCoverLinks.headingDescription"
            :subheading="adjustYourCoverLinks.subheading"
            :subheadingDescription="adjustYourCoverLinks.subheadingDescription"
            :links="updateTelephoneLinks(adjustYourCoverLinks.fields)"
            :isGreyBackground="adjustYourCoverLinks.isGreyBackground"
            :isArrowLinks="adjustYourCoverLinks.isArrowLinks"
            :iconSize="adjustYourCoverLinks.iconSize"
            :globalIcon="adjustYourCoverLinks.globalIcon"
            :globalButtonLabel="adjustYourCoverLinks.globalButtonLabel"
            :handleLinkClickEvent="true"
            @btnClick="(linkName) => onClick_AdjustYourCoverButton(linkName)"
          />
        </div>
        <div
          v-if="
            !filteredAdjustYourCoverLinks || filteredAdjustYourCoverLinks?.fields?.length == 0 || !currentPolicyLoaded
          "
          class="max-w-176 mt-4"
        >
          <Skeleton :blocks="skeletonBlocks" />
        </div>
      </template>
      <template #policySummary>
        <div class="mt-12" data-test-id="policy_summary_component">
          <PolicySummary
            v-if="currentPolicyLoaded"
            :currentPolicy="currentPolicy"
            :content="props.content"
          ></PolicySummary>
        </div>
      </template>
      <template #policyDetails>
        <div>
          <ProductDetailsPolicyDetails
            v-if="currentPolicyLoaded"
            :content="content"
            :currentPolicyTemp="currentPolicy"
            :selfServiceToggles="selfServiceToggles"
          ></ProductDetailsPolicyDetails>
        </div>
      </template>
      <template #paymentInformation>
        <ProductDetailsPaymentInformation
          v-if="currentPolicyLoaded"
          id="paymentInformation"
          :content="content"
          :currentPolicyTemp="currentPolicy"
          :selfServiceToggles="selfServiceToggles"
          :actionBarRuleResult="actionBarRuleResult"
        ></ProductDetailsPaymentInformation>
      </template>
      <template #cancelPolicyContact>
        <div>
          <LinkPanel
            v-if="
              currentPolicy.state.isPolicyCancelAllowed &&
              !disableInsuranceCancelPolicyButton &&
              !currentPolicy.cancellationDate &&
              enableContactUsInsuranceCancellation
            "
            class="mt-12"
            :heading="cancelPolicyContact.heading"
            :headingDescription="cancelPolicyContact.headingDescription"
            :subheading="cancelPolicyContact.subheading"
            :subheadingDescription="cancelPolicyContact.subheadingDescription"
            :links="cancelPolicyContact.fields"
            :isGreyBackground="cancelPolicyContact.isGreyBackground"
            :isArrowLinks="cancelPolicyContact.isArrowLinks"
            :iconSize="cancelPolicyContact.iconSize"
            :globalIcon="cancelPolicyContact.globalIcon"
            :globalButtonLabel="cancelPolicyContact.globalButtonLabel"
          ></LinkPanel>
        </div>
      </template>
      <template #cancelPolicy>
        <div>
          <LinkPanel
            :disabledPanel="isPolicyCancelDisabled"
            class="mt-12"
            :heading="cancelPolicy.heading"
            :headingDescription="cancelPolicy.headingDescription"
            :subheading="cancelPolicy.subheading"
            :subheadingDescription="cancelPolicy.subheadingDescription"
            :links="handleCancelInsuranceLink(cancelPolicy.fields)"
            :isGreyBackground="cancelPolicy.isGreyBackground"
            :isArrowLinks="cancelPolicy.isArrowLinks"
            :iconSize="cancelPolicy.iconSize"
            :globalIcon="cancelPolicy.globalIcon"
            :globalButtonLabel="cancelPolicy.globalButtonLabel"
          ></LinkPanel>
        </div>
      </template>
      <template #cancelCoverDisabledInfoBox>
        <div class="flex md:w-176 items-start mt-4">
          <InformationBox
            v-if="isPolicyCancelDisabled"
            :title="cancelCoverDisabledInfoBox.title.value"
            :description="cancelCoverDisabledInfoBox.content.value"
            :type="cancelCoverDisabledInfoBox.messageType.value"
            :isCircle="true"
            :useButtonSlot="false"
          ></InformationBox>
        </div>
      </template>
    </FormStepInputs>
  </div>
  <ModalDialog
    :modelValue="selectPropertyModal.modalIsOpen"
    :showXCloseButton="true"
    :closeOnEsc="true"
    size="md"
    @update:modelValue="selectPropertyModal_Close"
  >
    <SelectCoverageOption
      v-model="selectPropertyModal.model"
      :coverages="currentPolicy?.coverages ?? []"
      :fields="{
        title: selectPropertyModal?.content?.modalTitle,
        subtitle: selectPropertyModal?.content?.modalBody,
        homeIcon: productIcons?.homeIcon,
        homeContentsIcon: productIcons?.homeContentsIcon,
        contentsIcon: productIcons?.contentsIcon,
        motorIcon: productIcons?.motorIcon,
        motorThirdPartyIcon: productIcons?.motorThirdPartyIcon,
        motorThirdPartyFireIcon: productIcons?.motorThirdPartyFireIcon,
        boatIcon: productIcons?.boatIcon,
        caravanIcon: productIcons?.caravanIcon,
        motorcycleIcon: productIcons?.motorcycleIcon,
        petIcon: productIcons?.petIcon,
        modalBtnText: selectPropertyModal?.content?.callToActionButtonText,
      }"
      @buttonClicked="selectPropertyModal_Next"
    />
  </ModalDialog>
  <div v-show="isLoading">
    <Skeleton :blocks="skeletonBlocks" />
  </div>
</template>
<script setup lang="ts">
import { computedAsync } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';
import { InsurancePolicy, ISitecoreFormGenericInput } from '@/interfaces';
import { useI18n } from 'vue-i18n';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { useAccountStore } from '@/store';
import { getFormInput, getFormInputByName } from '@/utils/form-input-helper';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import { SelfServiceService } from '@/services/store/self-service-service';
import Skeleton from '@/components/Skeleton/Skeleton.vue';
import LinkPanel from '@/components/LinkPanel/LinkPanel.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { getPolicyStatusDetails } from '@/utils/self-service-rules/InsurancePolicyStatusRules';
import { getActionBarDisplayDetails } from '@/utils/self-service-rules/InsuranceActionBarRules';
import { isMobileDevice } from '@/utils/dom-helpers';
import { getCurrentPolicy } from './ProductDetailsHelper';
import { SelfServiceSessionPages } from '@/enums/self-service-session-pages';
import PolicySummary from './PolicySummary.vue';
import ProductDetailsPaymentInformation from './ProductDetailsPaymentInformation.vue';
import ClaimsPanel from '@/components/ClaimsPanel/ClaimsPanel.vue';
import StatusMessages from './StatusMessages/StatusMessages.vue';
import ProductDetailsPolicyDetails from './ProductDetailsPolicyDetails.vue';
import {
  DISABLE_INSURANCE_CANCEL_POLICY_BUTTON,
  ENABLE_CONTACT_US_INSURANCE_CANCELLATION,
} from '@/constants/feature-toggles';
import { useFeatureTogglesStore } from '../../../../store/feature-toggles/featureTogglesStore';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import SelectCoverageOption from '@/components/SelectCoverageOption/SelectCoverageOption.vue';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { FormEvents } from '@/enums/form-events.enum';
import { PaymentMethod } from '@/enums/payment-types.enum';

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;

const accountStore = useAccountStore();
const featureTogglesStore = useFeatureTogglesStore();

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
  testPolicyNumber?: string;
}>();

const currentPolicy = ref<InsurancePolicy>();
const adjustYourCoverLinks = ref<any>(getFormInput(props.content, 'adjustYourCoverLinks'));
const claimsIncomplete = ref<any>(getFormInputByName(props.content, 'claimsIncomplete'));
const cancelPolicy = ref<any>(getFormInput(props.content, 'cancelPolicy'));
const cancelPolicyContact = ref<any>(getFormInput(props.content, 'cancelPolicyContact'));
const cancelCoverDisabledInfoBox = ref<any>(getFormInput(props.content, 'cancelCoverDisabledInfoBox'));
const actionBarRuleResult = ref<any>();
const currentPolicyLoaded = ref<boolean>(false);

const policySummary = ref<any>();
const isLoading = ref<boolean>(props?.testPolicyNumber ? false : true);
const selfServiceToggles = ref<any>();
const enableContactUsInsuranceCancellation = ref(false);
const disableInsuranceCancelPolicyButton = ref(false);
const hasIncompleteClaims = ref(false);

const productIcons = ref<any>(getFormInputByName(props.content, 'productIcons'));
const selectPropertyModal = ref<any>({
  content: getFormInputByName(props.content, 'selectProperty'),
  model: '',
  modalIsOpen: false,
  linkItem: null,
});

enum AdjustYourCoverItems {
  switchToMonthlyPayments = 'switchToMonthlyPayments',
  addOptionalBenefits = 'addOptionalBenefits',
  keepYourPropertyDetailsUpToDate = 'keepYourPropertyDetailsUpToDate',
  hasSomethingChanged = 'hasSomethingChanged',
}

onMounted(async () => {
  const queryString = window.location.search.toLowerCase();
  const urlParams = new URLSearchParams(queryString);
  const policyNumber = urlParams.get('policynumber');

  if (policyNumber) {
    await accountStore.fetchIdentityDetails(true);
  }

  if (accountStore?.identityDetails?.portfolio?.insurancePolicies) {
    currentPolicy.value = getCurrentPolicy(
      accountStore.identityDetails?.portfolio?.insurancePolicies,
      props.testPolicyNumber
    );
    currentPolicyLoaded.value = true;
  }

  enableContactUsInsuranceCancellation.value = await featureTogglesStore.isFeatureToggleEnabled(
    ENABLE_CONTACT_US_INSURANCE_CANCELLATION
  );

  disableInsuranceCancelPolicyButton.value = await featureTogglesStore.isFeatureToggleEnabled(
    DISABLE_INSURANCE_CANCEL_POLICY_BUTTON
  );

  policySummary.value = getPolicySummaryModel();

  pushAnalytics();
});

function getPolicySummaryModel(): any {
  const tempPolicySummary = getFormInput(props.content, 'policySummary');
  if (!tempPolicySummary) return { title: '', icon: '', items: [] };
  return {
    title: tempPolicySummary.title,
    icon: tempPolicySummary.fields[0].icon,
    items: tempPolicySummary.fields[0].items,
  };
}

const handleCancelInsuranceLink = (cancelPolicyFields: any) => {
  const excludeName = enableContactUsInsuranceCancellation.value ? 'cancel' : 'contactUs';
  return cancelPolicyFields.filter((x: any) => x.name !== excludeName);
};

const hasIncompleteClaimsEmit = (response: boolean) => {
  hasIncompleteClaims.value = response;
};

const policyStatusRules = computedAsync(async () => {
  if (currentPolicy.value) {
    let renewalPolicy = undefined;
    if (currentPolicy.value?.state?.isInRenewal == true) {
      renewalPolicy = await accountStore.fetchPolicyRenewal(currentPolicy.value.policyNumber);
    }

    return await getPolicyStatusDetails(currentPolicy.value, new Date(), false, renewalPolicy);
  }
});

const isPolicyCancelDisabled = computed(() => {
  if (!currentPolicy.value) return true;

  return (
    !currentPolicy.value.state.isPolicyCancelAllowed ||
    disableInsuranceCancelPolicyButton.value ||
    currentPolicy.value?.cancellationDate !== undefined
  );
});

const filteredAdjustYourCoverLinks = computedAsync(async () => {
  if (!currentPolicy?.value && accountStore?.identityDetails?.portfolio?.insurancePolicies) {
    currentPolicy.value = getCurrentPolicy(
      accountStore.identityDetails.portfolio.insurancePolicies,
      props.testPolicyNumber
    );
  }

  try {
    if (currentPolicy.value) {
      selfServiceToggles.value = await SelfServiceService.getSelfServiceToggles();
      const tempAdjustYourCoverLinks = getFormInput(props.content, 'adjustYourCoverLinks');
      const validItems = [] as AdjustYourCoverItems[];

      let renewalPolicy = undefined;
      if (currentPolicy.value?.state?.isInRenewal == true) {
        renewalPolicy = await accountStore.fetchPolicyRenewal(currentPolicy.value.policyNumber);
      }

      const rulesResult = await getPolicyStatusDetails(currentPolicy.value, new Date(), true, renewalPolicy);

      actionBarRuleResult.value = await getActionBarDisplayDetails(currentPolicy.value, selfServiceToggles.value);

      if (actionBarRuleResult.value.showSwitchToMonthly) {
        validItems.push(AdjustYourCoverItems.switchToMonthlyPayments);
      }

      if (selfServiceToggles.value.enableViewBenefitsButton && rulesResult.showAddToCover) {
        validItems.push(AdjustYourCoverItems.addOptionalBenefits);
      }

      if (actionBarRuleResult.value.showUpdatePropertyDetails) {
        validItems.push(AdjustYourCoverItems.keepYourPropertyDetailsUpToDate);
      }

      validItems.push(AdjustYourCoverItems.hasSomethingChanged);

      const tempFilteredFields = tempAdjustYourCoverLinks;

      tempFilteredFields.fields = tempAdjustYourCoverLinks.fields.filter((x: any) => validItems.includes(x.name));

      isLoading.value = false;
      return tempFilteredFields;
    }
  } catch (error) {
    console.error('error', error);
  }

  return null;
});

const onClick_AdjustYourCoverButton = async (linkItem: any) => {
  if (!currentPolicy?.value) return;

  let redirectUrl;
  let response;
  let coverageId;

  switch (linkItem.name) {
    case AdjustYourCoverItems.switchToMonthlyPayments:
      response = await SelfServiceService.createQuoteSession(
        currentPolicy.value.policyNumber,
        SelfServiceSessionPages.ChangeMonthlyPaymentPage
      );
      if (response?.success) {
        redirectUrl = `${linkItem?.link?.value?.href}${linkItem?.link?.value?.querystring.replace('{0}', response.data)}`;
        window.location.href = redirectUrl;
      }
      break;
    case AdjustYourCoverItems.addOptionalBenefits:
      redirectUrl = `${linkItem?.link?.value?.href.replace('{0}', currentPolicy.value.policyNumber)}`;
      window.location.href = redirectUrl;
      break;
    case AdjustYourCoverItems.keepYourPropertyDetailsUpToDate:
      if (currentPolicy.value.coverages.length > 1) {
        await pushModalAnalytics();

        selectPropertyModal.value.linkItem = linkItem;
        selectPropertyModal.value.modalIsOpen = true;
      } else {
        coverageId = currentPolicy.value.coverages[0].id;
        handleUpdatePropertyDetailsRedirect(coverageId, linkItem);
      }

      break;
    case AdjustYourCoverItems.hasSomethingChanged:
      if (isMobileDevice()) {
        window.location.href = linkItem.link.value.href;
      }
      break;
  }
};

function updateTelephoneLinks(fields: any[]) {
  fields.forEach((field) => {
    field.link.value.href =
      field.link.value.href.indexOf('tel:') > -1
        ? field.link.value.href.replace('https://', '').replace('http://', '')
        : field.link.value.href;
  });
  return fields;
}

const handleUpdatePropertyDetailsRedirect = (coverageId: string, linkItem: any) => {
  if (!currentPolicy.value) return;
  const redirectUrl = `${linkItem?.link?.value?.href?.replace('{0}', currentPolicy.value.policyNumber)?.replace('{1}', coverageId)}`;
  window.location.href = redirectUrl;
};

const selectPropertyModal_Next = (value: boolean) => {
  selectPropertyModal.value.modalIsOpen = false;

  const coverageId = selectPropertyModal.value.model;
  const coverage = currentPolicy.value?.coverages.find((x) => x.id === coverageId);

  if (coverage) {
    handleUpdatePropertyDetailsRedirect(coverageId, selectPropertyModal.value.linkItem);
  }
};

function selectPropertyModal_Close(e: any) {
  selectPropertyModal.value.modalIsOpen = false;
}

const pushAnalytics = async () => {
  let currentPolicyPaymentMethods = null;
  let currentTerm = 0;
  if (currentPolicy.value?.state.isInRenewal) {
    currentTerm = currentPolicy.value!.renewalTermPaymentDetails?.termNumber;
  } else {
    currentTerm = currentPolicy.value!.currentTermPaymentDetails?.termNumber;
  }

  if (currentTerm) {
    try {
      currentPolicyPaymentMethods = await SelfServiceService.getPaymentMethods(
        currentPolicy.value!.policyNumber!,
        currentTerm
      );
    } catch (error) {
      console.error(error);
    }
  }

  const paymentMethod =
    PaymentMethod.BankAccount === currentPolicyPaymentMethods?.paymentMethod &&
    currentPolicyPaymentMethods.bankAccountDetails !== null
      ? 'Bank account'
      : currentPolicyPaymentMethods?.paymentMethod === PaymentMethod.CreditCard &&
          currentPolicyPaymentMethods.creditCardDetails !== null
        ? 'Credit card'
        : 'Unknown';

  const pageAnalytics = new PageAnalyticsBuilder('Insurance Product Details');
  pageAnalytics.populateProducts(dictionary, policyStatusRules.value, currentPolicy.value?.policyNumber, paymentMethod);
  pageAnalytics.pushAnalytics();
};

const pushModalAnalytics = async () => {
  if (!currentPolicy?.value) return;

  const isAuthenticated = await accountStore.isAuthenticated;
  const pageAnalytics = new PageAnalyticsBuilder('Insurance Product Details');
  pageAnalytics.populateModalStart(
    FormEvents.START,
    isAuthenticated,
    'Insurance Product Details',
    'Combined policy property update modal',
    currentPolicy.value.termEnd ?? undefined,
    accountStore.identityDetails?.d365Id
  );
  pageAnalytics.pushAnalytics();
};

const skeletonBlocks = [
  {
    height: 26,
    width: 338,
    class: 'mb-3',
  },
  {
    height: 26,
    width: 176,
    class: 'mb-3',
  },
  {
    height: 88,
    width: 88,
    class: 'mb-3',
  },
  {
    height: 36,
    width: 244,
    class: 'mb-2',
  },
  {
    height: 36,
    width: 244,
    class: 'mb-6',
  },
  {
    height: 48,
    width: 204,
    class: 'mb-6',
  },
  {
    height: 26,
    width: 204,
    class: 'mb-5',
  },
];
</script>
